
import { computed, defineComponent, getCurrentInstance, ref, watch } from 'vue'
import config from '@root/config'
import SpinnerLoader from '../../../components/SpinnerLoader.vue'
import Breadcrumbs from '../../../components/Breadcrumbs.vue'
import { useProject } from '../../../hooks/useProject'
import { useMarketplaceStore } from '../../../store/stores/marketplace'
import { useBillingStore } from '@/store/stores/billing'
import useRouter from '../../../hooks/useRouter'
import IntegrationFeedback from '../components/IntegrationFeedback.vue'
import IntegrationInfo from '../components/IntegrationInfo.vue'
import IntegrationConnect from '../components/IntegrationConnect.vue'
import IntegrationPricingTiers from '../components/IntegrationPricingTiers.vue'
import IntegrationOtherProducts from '../components/IntegrationOtherProducts.vue'
import PaymentSidebar from '../components/billing/PaymentSidebar.vue'
import IntegrationRequestSidebar from '../components/IntegrationRequestSidebar.vue'
import UxIcon from '../components/UxIcon.vue'
import { useTabs } from '../../../hooks/useTabs'
import { declOfNum } from '@/utils'
import store from '@/store/index'
import { useIntegrationPageUx } from '@/helpers/marketplace/useIntegrationPageUx'
import { buildOtherIntegrationItems } from '@/helpers/marketplace/otherIntegrations'
import { formatInstallCount } from '@/helpers/marketplace/integrationPurchaseCount'
import { isBlockedBySmartBotTariff, isDhChatBlockedByProjectState, projectHasYClientsConnector } from '@/helpers/marketplace/dhChatVisibility'
import {
    getMarketplaceProjectKeyFromRoute,
    hasAtLeastOneProject,
    isPublicMarketplacePath,
} from '../marketplaceRouteGuards'
import { usePublicMarketplacePageSync } from '../usePublicMarketplacePageSync'
import { getPendingSlots } from '@/api/networkFixer'

export default defineComponent({
    components: {
        SpinnerLoader,
        Breadcrumbs,
        IntegrationConnect,
        IntegrationPricingTiers,
        IntegrationOtherProducts,
        IntegrationInfo,
        IntegrationFeedback,
        PaymentSidebar,
        IntegrationRequestSidebar,
        UxIcon,
    },
    props: {},
    setup(props, { emit }) {
        const { project } = useProject()
        const requestSidebarOpened = ref(false)
        const billingStore = useBillingStore()
        const { route, router } = useRouter()
        const root = getCurrentInstance()!.proxy as { $auth?: { isAuthenticated: boolean } }
        const isAuthenticated = computed(() => Boolean(root.$auth?.isAuthenticated))
        const marketplaceStore = useMarketplaceStore()
        const marketplaceProjectKey = computed(() =>
            getMarketplaceProjectKeyFromRoute(route.value.params as { id?: string }),
        )
        const isPublicMarketplaceRoute = computed(() => isPublicMarketplacePath(route.value.path))
        const allIntgrations = computed(() => marketplaceStore.allIntgrations(marketplaceProjectKey.value).map( integration => {
            if (isPublicMarketplaceRoute.value) {
                if (!isAuthenticated.value) {
                    integration.status = 'unauthorized'
                } else if (!hasAtLeastOneProject()) {
                    integration.status = 'noProject'
                }
            }
            return integration
        }))

        const integrationIdParam = computed(() => String(route.value.params.integrationId ?? ''))

        const integration = computed(() =>
            allIntgrations.value.find(el => String(el.id) === integrationIdParam.value)
        )
        const integrationFeedback = computed(() => {
            return marketplaceStore.integrationFeedback(integrationIdParam.value)
        })

        const {
            pageUx,
            headerIcon,
            headerBadge,
            headerCategory,
            headerTitle,
            headerDescription,
            headerExtraInfoHtml,
            connectFacts,
            connectPartner,
            showDhOfficial,
            pricingTiers,
            otherIntegrationRefs,
            otherIntegrationsTitle,
            connectCard,
        } = useIntegrationPageUx(integration)

        const otherIntegrationItems = computed(() =>
            buildOtherIntegrationItems(
                otherIntegrationRefs.value,
                allIntgrations.value.filter(item => item.published !== false),
                integrationIdParam.value,
                project.value?.id,
            ),
        )

        const hasRating = computed(() => Number(integration.value?.feedbackCount || 0) > 0)
        const ratingValue = computed(() => Number(integration.value?.feedbackRating || 0))
        const ratingLabel = computed(() => ratingValue.value.toFixed(1))
        const ratingPercent = computed(() => (Math.min(5, Math.max(0, ratingValue.value)) / 5) * 100)
        const installsLabel = computed(() => formatInstallCount(integration.value))
        const priceDisplay = computed(() => {
            if (!integration.value?.price) return { type: 'free' as const }
            const rawPeriod = (integration.value.pricePeriod || '').toString().trim().toLowerCase()
            const amount = Number(integration.value.price).toLocaleString('ru-RU')
            if (rawPeriod === '0' || rawPeriod === 'once') {
                return { type: 'once' as const, text: `От ${amount} ₽` }
            }
            const periodMap: Record<string, string> = {
                month: ' ₽/мес',
                months: ' ₽/мес',
                monthly: ' ₽/мес',
                мес: ' ₽/мес',
                месяц: ' ₽/мес',
            }
            const period = periodMap[rawPeriod] || integration.value.pricePeriod || ' ₽/мес'
            return { type: 'period' as const, text: `От ${amount} ${period || ''}`.trim() }
        })
        const sidebarPriceDisplay = computed(() => {
            const uxPrice = connectCard.value?.price
            if (uxPrice !== null && uxPrice !== undefined && uxPrice !== '') {
                return { type: 'custom' as const, text: uxPrice }
            }
            return priceDisplay.value
        })
        const trialLabel = computed(() => {
            const days = Number(integration.value?.trialDays || 0)
            if (!days) return ''
            return `Первые ${days} ${declOfNum(days, ['день', 'дня', 'дней'])} бесплатно`
        })
        const feedbackTabCount = computed(() =>
            Number(integration.value?.feedbackCount || 0).toLocaleString('ru-RU')
        )
        const moduleIds = config.marketplaceModulesIds[config.buildType]
        const isDhChatIntegration = computed(
            () => String(integration.value?.id || '') === String(moduleIds.dhChat),
        )
        const isCustomCascadeIntegration = computed(
            () => String(integration.value?.id || '') === String(moduleIds.customCascades),
        )
        const isZvonoBotIntegration = computed(
            () => String(integration.value?.id || '') === String(moduleIds.zvonoBot),
        )
        const isOpz2Integration = computed(
            () => String(integration.value?.id || '') === String(moduleIds.opz2),
        )
        const isOpz2KpiSettingsIntegration = computed(
            () => String(integration.value?.id || '') === String(moduleIds.opz2KPISettings),
        )
        const isHotWindowsIntegration = computed(
            () => String(integration.value?.id || '') === String(moduleIds.hotWindows),
        )
        const isDhChatConnected = computed(
            () => isDhChatIntegration.value && integration.value?.status === 'installed',
        )
        const isCustomCascadeConnected = computed(
            () => isCustomCascadeIntegration.value && integration.value?.status === 'installed',
        )
        const pendingPaymentInProgress = ref(false)
        const pendingPaymentIntegrationIds = [
            String(moduleIds.dhChat),
            String(moduleIds.customCascades),
            String(moduleIds.zvonoBot),
            String(moduleIds.businessPulseReport),
            String(moduleIds.opz2),
            String(moduleIds.hotWindows),
        ]
        const billingEnabled = computed(() => Boolean(project.value?.billingEnabled))
        const isDhChatRequestMode = computed(() =>
            isDhChatIntegration.value &&
            ['unauthorized', 'noProject'].includes(String(integration.value?.status || '')),
        )
        const isCustomCascadeRequestMode = computed(() =>
            isCustomCascadeIntegration.value &&
            ['unauthorized', 'noProject'].includes(String(integration.value?.status || '')),
        )
        const showDhChatExtraActions = computed(
            () =>
                isDhChatIntegration.value &&
                !isDhChatConnected.value &&
                !isDhChatRequestMode.value &&
                billingEnabled.value,
        )
        const showCustomCascadeExtraActions = computed(
            () =>
                isCustomCascadeIntegration.value &&
                !isCustomCascadeConnected.value &&
                !isCustomCascadeRequestMode.value &&
                billingEnabled.value,
        )
        const dhChatHint = computed(() =>
            isDhChatIntegration.value ? 'Для «Умного Бота» функция уже входит в тариф' : '',
        )
        const isConnectBlockedByPendingPayment = computed(() => {
            if (integration.value?.status !== 'uninstalled' || !pendingPaymentInProgress.value) {
                return false
            }
            return isDhChatIntegration.value || isCustomCascadeIntegration.value || isZvonoBotIntegration.value
        })
        const connectDisabledTooltip = computed(() =>
            isConnectBlockedByPendingPayment.value ? 'Оплата в обработке' : '',
        )
        const onPendingPaymentDetected = () => {
            pendingPaymentInProgress.value = true
        }
        const currentBillingTariff = computed(() => {
            if (!project.value?.id) return undefined
            return billingStore.getCurrentTariff(project.value.id)
        })
        const isModuleHiddenByProjectState = computed(() => {
            const current = currentBillingTariff.value
            const tariffIds = {
                baseTariffId: current?.baseTariffId ?? null,
                currentTariffId: current?.integrationId || null,
            }
            if (isDhChatIntegration.value) {
                return isDhChatBlockedByProjectState({
                    project: { connectors: project.value?.connectors as unknown[] | undefined },
                    ...tariffIds,
                })
            }
            if (isCustomCascadeIntegration.value) {
                return isBlockedBySmartBotTariff(tariffIds)
            }
            if (isOpz2Integration.value || isOpz2KpiSettingsIntegration.value) {
                return !projectHasYClientsConnector(project.value)
            }
            return false
        })
        const resolveAllowedTariffPeriod = (rawPeriod: unknown) => {
            const normalizedRaw = Number(rawPeriod) || 1
            const allowed = [1, 3, 6, 12, 24]
            const resolved = allowed.reduce((acc, value) =>
                value <= normalizedRaw ? value : acc,
            1)
            return String(resolved)
        }
        const openDhChatTariffUpgrade = () => {
            const projectId = project.value?.id || marketplaceProjectKey.value
            if (!projectId || projectId === 'unauthorized') {
                requestSidebarOpened.value = true
                return
            }

            const period = resolveAllowedTariffPeriod(currentBillingTariff.value?.periodInMonths)
            const targetUrl = `/project/${projectId}/billing/?section=tariffs&default=tariff2&period=${period}`
            window.open(targetUrl, '_blank', 'noopener')
        }
        const goToMaxBusinessIntegration = async () => {
            const currentId = integrationIdParam.value
            const maxBusinessId = String(moduleIds.maxBusiness)

            if (!currentId || !route.value.path.includes(currentId)) {
                requestSidebarOpened.value = true
                return
            }

            await router.push(route.value.path.replace(currentId, maxBusinessId))
        }

        watch(
            isModuleHiddenByProjectState,
            async shouldRedirect => {
                if (!shouldRedirect) return

                const targetPath = marketplaceProjectKey.value !== 'unauthorized'
                    ? `/project/${marketplaceProjectKey.value}/marketplace`
                    : '/marketplace'

                if (route.value.path === targetPath) return

                await router.replace(targetPath)
            },
            { immediate: true },
        )
        const integrationTabs = computed(() => [
            {
                tabName: 'Информация',
                sectionName: 'info',
                component: IntegrationInfo,
            },
            {
                tabName: 'Отзывы',
                sectionName: 'feedback',
                component: IntegrationFeedback,
            },
        ])
        const { activeTab, tabs } = useTabs({
            tabs: integrationTabs,
            defaultSectionName: 'info',
        })

        const breadcrumbs = computed(() => {
            const projectForBreadcrumbs =
                project.value ||
                (marketplaceProjectKey.value !== 'unauthorized'
                    ? store.getters.projectById(marketplaceProjectKey.value)
                    : null)

            if (!projectForBreadcrumbs) {
                const brUnAuth = [
                    {
                        text: 'Маркетплейс',
                        to: '/marketplace',
                        disabled: false,
                    },
                    {
                        text: integration.value?.name ?? 'ЗАГРУЗКА...',
                        to: '',
                        disabled: true,
                    },
                ]
                return brUnAuth
            } else {
                const br = [
                    {
                        text: projectForBreadcrumbs.name,
                        to: `/project/${projectForBreadcrumbs.id}`,
                        type: 'project'
                    },
                    {
                        text: 'Маркетплейс',
                        to: `/project/${projectForBreadcrumbs.id}/marketplace`,
                        disabled: false,
                    },
                ]
                return br
            }

        })

        const loading = ref(false)

        const loadIntegrationPage = async () => {
            const projectKey = marketplaceProjectKey.value
            const integrationId = integrationIdParam.value
            const promises: Promise<unknown>[] = []

            // При каждом заходе на страницу интеграции обновляем список,
            // чтобы статус (например, dhChat после оплаты) не оставался устаревшим.
            promises.push(marketplaceStore.fetchIntegrations(projectKey))
            if (!marketplaceStore.integrationFeedback(integrationId).length) {
                promises.push(marketplaceStore.fetchFeedback(integrationId))
            }
            const projectId = marketplaceProjectKey.value
            if (projectId !== 'unauthorized') {
                const projectForBilling = store.getters.projectById(projectId)
                if (projectForBilling?.billingEnabled) {
                    promises.push(billingStore.fetchCurrentTariff(projectId))
                }
                if (pendingPaymentIntegrationIds.includes(String(integrationId))) {
                    promises.push(
                        (async () => {
                            const { data, error } = await getPendingSlots({ projectId })
                            if (error || !Array.isArray(data)) {
                                pendingPaymentInProgress.value = false
                                return
                            }
                            const currentIntegrationId = String(integrationId)
                            pendingPaymentInProgress.value = data.some((payload: any) => {
                                const payloadIntegrationId = String(
                                    payload?.integration_id ||
                                        payload?.price?.lineItems?.[0]?.integrationId ||
                                        '',
                                )
                                return payloadIntegrationId === currentIntegrationId
                            })
                        })(),
                    )
                } else {
                    pendingPaymentInProgress.value = false
                }
            } else {
                pendingPaymentInProgress.value = false
            }
            await Promise.all(promises)
        }

        const hasCachedIntegrationData = () => {
            const integrationId = integrationIdParam.value
            return marketplaceStore.allIntgrations(marketplaceProjectKey.value).some(
                el => String(el.id) === integrationId,
            )
        }

        usePublicMarketplacePageSync(loadIntegrationPage, loading, {
            hasCachedData: hasCachedIntegrationData,
        })
        const paymentModalRenewal = computed(() => billingStore.paymentModal.renewal)
        const paymentSidebarMode = computed(() =>
            billingStore.addChannelContext?.name ? 'add-channel' : 'billing'
        )

        return {
            billingEnabled,
            requestSidebarOpened,
            paymentModalRenewal,
            paymentSidebarMode,
            loading,
            breadcrumbs,
            integration,
            integrationIdParam,
            allIntgrations,
            activeTab,
            tabs,
            integrationFeedback,
            headerIcon,
            headerBadge,
            headerCategory,
            headerTitle,
            headerDescription,
            headerExtraInfoHtml,
            hasRating,
            ratingLabel,
            ratingPercent,
            installsLabel,
            sidebarPriceDisplay,
            trialLabel,
            connectFacts,
            connectPartner,
            showDhOfficial,
            pricingTiers,
            otherIntegrationItems,
            otherIntegrationsTitle,
            feedbackTabCount,
            isDhChatIntegration,
            isCustomCascadeIntegration,
            isDhChatConnected,
            isConnectBlockedByPendingPayment,
            connectDisabledTooltip,
            onPendingPaymentDetected,
            showDhChatExtraActions,
            showCustomCascadeExtraActions,
            dhChatHint,
            openDhChatTariffUpgrade,
            goToMaxBusinessIntegration,
        }
    },
})
